import {Inject, Injectable} from '@angular/core';
import {AuditId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';


@Injectable({
    providedIn: 'root'
})
export class UserCustomizationService {

    private readonly PROJECT_VISIT_CACHE_SIZE = 3;
    private readonly AUDIT_VISIT_CACHE_SIZE = 3;
    private readonly LOCALSTORAGE_PROJECT_VISIT_CACHE_KEY = "_webmate_project_visit_cache";
    private readonly LOCALSTORAGE_PROJECT_ROUTE_CACHE_KEY = "_webmate_project_route_cache_";
    private readonly LOCALSTORAGE_AUDIT_VISIT_CACHE_KEY = "_webmate_audit_visit_cache_";

    constructor(private userService: UserService, private persistenceService: PersistenceService) {
    }

    private retrieveMostRecentProjectsFromCache(): Observable<ProjectId[]> {
        const ids = JSON.parse(this.persistenceService.getItem(this.LOCALSTORAGE_PROJECT_VISIT_CACHE_KEY)) || [];
        // Only return projects the user still has access to
        return this.userService.getUserProjects().pipe(map(userProjects => ids.filter(id => userProjects.includes(id))));
    }

    getMostRecentProjects(): Observable<ProjectId[]> {
        return this.retrieveMostRecentProjectsFromCache();
    }

    getMostRecentProject(): Observable<ProjectId | undefined> {
        return this.retrieveMostRecentProjectsFromCache().pipe(map(ids => ids[0]));
    }

    updateMostRecentProject(id: ProjectId): void {
        this.retrieveMostRecentProjectsFromCache().pipe(take(1)).subscribe(projects => {
            // remove id if it exists
            let updatedProjects = projects.filter(i => i !== id);
            // insert it at the first position, i.e. the most recent
            updatedProjects.unshift(id);
            // limit cache size to configured value
            updatedProjects = updatedProjects.slice(0, this.PROJECT_VISIT_CACHE_SIZE);
            this.persistenceService.setItem(this.LOCALSTORAGE_PROJECT_VISIT_CACHE_KEY, JSON.stringify(updatedProjects));
        })
    }

    getMostRecentAudits(projectId: ProjectId): AuditId[] {
        return this.readAuditVisitCache(projectId);
    }

    updateMostRecentAudit(projectId: ProjectId, auditId: AuditId): void {
        const audits = this.readAuditVisitCache(projectId).filter(id => id !== auditId);
        audits.unshift(auditId);
        this.writeAuditVisitCache(projectId, audits);
    }

    setMostRecentAudits(projectId: ProjectId, auditIds: AuditId[]): void {
        const distinctAuditIds = auditIds.filter((id, index) => auditIds.indexOf(id) === index);
        this.writeAuditVisitCache(projectId, distinctAuditIds);
    }

    getMostRecentSubrouteForProject(id: ProjectId): string {
        return this.persistenceService.getItem(this.LOCALSTORAGE_PROJECT_ROUTE_CACHE_KEY + id) || '';
    }

    updateMostRecentSubrouteForProject(id: ProjectId, subroute: string) {
        this.persistenceService.setItem(this.LOCALSTORAGE_PROJECT_ROUTE_CACHE_KEY + id, subroute);
    }

    private readAuditVisitCache(projectId: ProjectId): AuditId[] {
        const cached = this.persistenceService.getItem(this.LOCALSTORAGE_AUDIT_VISIT_CACHE_KEY + projectId);
        if (!cached) {
            return [];
        }
        try {
            const auditIds = JSON.parse(cached);
            return Array.isArray(auditIds) ? auditIds.filter((id): id is AuditId => typeof id === 'string') : [];
        } catch {
            return [];
        }
    }

    private writeAuditVisitCache(projectId: ProjectId, auditIds: AuditId[]): void {
        this.persistenceService.setItem(
            this.LOCALSTORAGE_AUDIT_VISIT_CACHE_KEY + projectId,
            JSON.stringify(auditIds.slice(0, this.AUDIT_VISIT_CACHE_SIZE))
        );
    }

}
